import React, { useState, useEffect } from 'react';
import axios from '../config/axios';
import '../assets/styles/AdminDashboard.css';

const AdminDashboard = ({ user, onLogout }) => {
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [publishData, setPublishData] = useState({ title: '', body: '', target: 'all' });
    const [publishing, setPublishing] = useState(false);
    const [message, setMessage] = useState(null);
    const [error, setError] = useState(null);

    // Smart Store States
    const [suggestions, setSuggestions] = useState([]);
    const [loadingSuggestions, setLoadingSuggestions] = useState(false);

    // Feedback Management States
    const [feedbackList, setFeedbackList] = useState([]);
    const [feedbackStats, setFeedbackStats] = useState(null);
    const [feedbackLoading, setFeedbackLoading] = useState(false);
    const [feedbackFilter, setFeedbackFilter] = useState('');

    useEffect(() => {
        if (user?.role === 'admin') {
            fetchAnalytics();
            fetchSuggestions();
            fetchFeedback();
        } else {
            setLoading(false); // Stop loading so we can show the "Re-login required" warning
        }
    }, [user]);

    const fetchFeedback = async () => {
        try {
            setFeedbackLoading(true);
            const [listResp, statsResp] = await Promise.all([
                axios.get('/api/v1/admin/feedback'),
                axios.get('/api/v1/admin/feedback/stats')
            ]);
            setFeedbackList(listResp.data || []);
            setFeedbackStats(statsResp.data);
        } catch (err) {
            console.error('Failed to fetch feedback:', err);
        } finally {
            setFeedbackLoading(false);
        }
    };

    const updateFeedbackStatus = async (id, status, notes = '') => {
        try {
            await axios.put(`/api/v1/admin/feedback/${id}`, { status, admin_notes: notes });
            setMessage({ type: 'success', text: 'Feedback updated!' });
            fetchFeedback();
        } catch (err) {
            setMessage({ type: 'error', text: 'Failed to update feedback.' });
        }
    };

    const fetchSuggestions = async () => {
        try {
            setLoadingSuggestions(true);
            // In a real scenario, we might pick a specific kid, but for admin we fetch general 5-18 trending items
            const response = await axios.get('/api/v1/admin/store/suggestions?kid_id=all');
            setSuggestions(response.data.suggestions || []);
        } catch (err) {
            console.error('Failed to fetch suggestions:', err);
        } finally {
            setLoadingSuggestions(false);
        }
    };

    const handleAddProduct = async (product) => {
        try {
            await axios.post('/api/v1/admin/store/products', {
                ...product,
                is_active: true
            });
            setMessage({ type: 'success', text: `${product.name} added to store!` });
        } catch (err) {
            setMessage({ type: 'error', text: 'Failed to stock item.' });
        }
    };

    const fetchAnalytics = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/v1/admin/analytics');
            setAnalytics(response.data);
            setError(null);
        } catch (error) {
            console.error('Failed to fetch analytics:', error);
            if (error.response?.status === 403) {
                setError('Access Denied: You need admin privileges.');
            } else {
                setError('Failed to load analytics data.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handlePublish = async (e) => {
        e.preventDefault();
        setPublishing(true);
        setMessage(null);
        try {
            const response = await axios.post('/api/v1/admin/publish', publishData);
            const count = response.data.recipients || '0';
            const msg = response.data.message || `Broadcast sent to ${count} users.`;
            setMessage({ type: 'success', text: msg });
            setPublishData({ title: '', body: '', target: 'all' });
        } catch (error) {
            console.error('Broadcast failed:', error);
            const errorMsg = error.response?.data?.message || error.response?.data || 'Failed to send broadcast.';
            setMessage({ type: 'error', text: errorMsg });
        } finally {
            setPublishing(false);
        }
    };

    if (loading) {
        return (
            <div className="admin-dashboard">
                <div className="loading-state">
                    <div className="spinner"></div>
                    <p>Loading market intelligence...</p>
                </div>
            </div>
        );
    }

    // SVG Bar Chart Component for Adoption
    const AdoptionChart = ({ stats }) => {
        if (!stats || stats.length === 0) return <p>No adoption data yet.</p>;
        const max = Math.max(...stats.map(s => s.count)) || 1;

        return (
            <div className="chart-container">
                {stats.map((stat, i) => (
                    <div key={i} className="chart-bar-wrapper">
                        <div
                            className="chart-bar"
                            style={{ height: `${(stat.count / max) * 100}%` }}
                            title={`${stat.count} users on ${stat.date}`}
                        ></div>
                        <span className="chart-label">{stat.date.split('-').slice(1).join('/')}</span>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className="admin-dashboard">
            <header className="admin-header">
                <div>
                    <h1>Admin Command Center</h1>
                    <p className="admin-subtitle">Global Adoption & Retention Metrics</p>
                </div>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <button
                        onClick={() => window.location.href = '/'}
                        className="admin-switch-btn"
                        aria-label="Switch to parent view"
                        style={{
                            background: 'rgba(255, 255, 255, 0.1)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            color: 'white',
                            padding: '8px 16px',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '0.9rem',
                            fontWeight: '500',
                            transition: 'all 0.2s'
                        }}
                        onMouseOver={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
                        onMouseOut={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.1)'}
                    >
                        🏠 Parent View
                    </button>
                    <button onClick={onLogout} className="admin-logout" aria-label="Log out">Logout</button>
                </div>
            </header>

            {user?.role !== 'admin' && (
                <div className="admin-warning-card">
                    <div className="warning-icon">⚠️</div>
                    <div className="warning-text">
                        <h3>Elevated Access Required</h3>
                        <p>Your current session is active as a <strong>{user?.role}</strong>. Since you were recently promoted, you must <strong>logout and log back in</strong> to activate your admin privileges.</p>
                        <button onClick={onLogout} className="btn-relogin">Log Out Now</button>
                    </div>
                </div>
            )}

            {error && (
                <div role="alert" style={{
                    background: 'rgba(239, 68, 68, 0.1)',
                    color: '#ef4444',
                    padding: '1rem',
                    borderRadius: '0.5rem',
                    border: '1px solid #ef4444',
                    marginBottom: '2rem'
                }}>
                    <strong>Error:</strong> {error}
                </div>
            )}

            <div className="admin-stats-grid">
                <div className="stat-card">
                    <div className="stat-label">Total Households</div>
                    <div className="stat-value">{analytics?.total_households || 0}</div>
                </div>
                <div className="stat-card">
                    <div className="stat-label">Active Users</div>
                    <div className="stat-value">{analytics?.total_users || 0}</div>
                </div>
                <div className="stat-card">
                    <div className="stat-label">Chores Completed</div>
                    <div className="stat-value">{analytics?.engagement?.chores_completed || 0}</div>
                </div>
                <div className="stat-card">
                    <div className="stat-label">Active Goals</div>
                    <div className="stat-value">{analytics?.engagement?.active_goals || 0}</div>
                </div>
            </div>

            <div className="admin-main-grid">
                <div className="dashboard-card">
                    <h2>User Adoption (Last 30 Days)</h2>
                    <AdoptionChart stats={analytics?.adoption_stats} />
                </div>

                <div className="dashboard-card">
                    <h2>Retention Funnel</h2>
                    <div className="retention-grid">
                        <div className="retention-item">
                            <div className="stat-label">Day 1</div>
                            <div className="stat-value">{analytics?.retention_rates?.day_1?.toFixed(1) || '0.0'}%</div>
                            <div className="retention-target">Goal: 35%</div>
                        </div>
                        <div className="retention-item">
                            <div className="stat-label">Day 7</div>
                            <div className="stat-value">{analytics?.retention_rates?.day_7?.toFixed(1) || '0.0'}%</div>
                            <div className="retention-target">Goal: 22%</div>
                        </div>
                        <div className="retention-item">
                            <div className="stat-label">Day 30</div>
                            <div className="stat-value">{analytics?.retention_rates?.day_30?.toFixed(1) || '0.0'}%</div>
                            <div className="retention-target">Goal: 12%</div>
                        </div>
                    </div>
                    <p style={{ marginTop: '1.5rem', fontSize: '0.85rem', color: '#94a3b8' }}>
                        * Industry benchmarks based on 2026 Parenting/Finance niche data.
                    </p>
                </div>

                <div className="dashboard-card" style={{ gridColumn: 'span 2' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                        <h2>Smart Store Management</h2>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button
                                onClick={async () => {
                                    if (!window.confirm('This will DELETE all products from the database. The store will then show fresh default products. Continue?')) return;
                                    setLoadingSuggestions(true);
                                    try {
                                        const resp = await axios.post('/api/v1/admin/store/reset');
                                        setMessage({ type: 'success', text: resp.data.message });
                                        fetchSuggestions();
                                    } catch (err) {
                                        setMessage({ type: 'error', text: 'Reset failed.' });
                                    } finally {
                                        setLoadingSuggestions(false);
                                    }
                                }}
                                className="admin-reset-btn"
                                style={{
                                    background: '#ef4444',
                                    color: 'white',
                                    border: 'none',
                                    padding: '6px 14px',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '0.85rem',
                                    fontWeight: '600'
                                }}
                            >
                                🗑️ Reset Catalog
                            </button>
                            <button
                                onClick={async () => {
                                    setLoadingSuggestions(true);
                                    try {
                                        const resp = await axios.post('/api/v1/admin/store/sync');
                                        setMessage({ type: 'success', text: resp.data.message });
                                        fetchSuggestions();
                                    } catch (err) {
                                        setMessage({ type: 'error', text: 'Sync failed.' });
                                    } finally {
                                        setLoadingSuggestions(false);
                                    }
                                }}
                                className="admin-sync-btn"
                                style={{
                                    background: '#10b981',
                                    color: 'white',
                                    border: 'none',
                                    padding: '6px 14px',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '0.85rem',
                                    fontWeight: '600'
                                }}
                            >
                                🔄 Sync Catalog
                            </button>
                            <span className="badge">PA-API Fallback Active</span>
                        </div>
                    </div>

                    <div className="store-management-tabs">
                        <div className="admin-form-section">
                            <h3>Add Custom Reward</h3>
                            <form className="admin-inline-form" onSubmit={async (e) => {
                                e.preventDefault();
                                const formData = new FormData(e.target);
                                try {
                                    await axios.post('/api/v1/admin/store/products', {
                                        name: formData.get('name'),
                                        category: formData.get('category'),
                                        price_cents: Math.round(parseFloat(formData.get('price')) * 100),
                                        image_url: formData.get('image_url'),
                                        asin: formData.get('asin')
                                    });
                                    setMessage({ type: 'success', text: 'Product added to shop!' });
                                    e.target.reset();
                                } catch (err) {
                                    setMessage({ type: 'error', text: 'Failed to add product.' });
                                }
                            }}>
                                <input name="name" placeholder="Product Name" required />
                                <select name="category" required>
                                    <option value="gaming">Gaming</option>
                                    <option value="toys">Toys</option>
                                    <option value="creative">Creative</option>
                                    <option value="electronics">Electronics</option>
                                    <option value="books">Books</option>
                                </select>
                                <input name="price" type="number" step="0.01" placeholder="Price ($)" required />
                                <input name="image_url" placeholder="Image URL (optional)" />
                                <button type="submit" className="btn-primary">Add Item</button>
                            </form>
                        </div>

                        <div className="admin-suggestions-section" style={{ marginTop: '2rem' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <h3>Smart Suggestions (Ages 5-18)</h3>
                                <p style={{ fontSize: '0.8rem', color: '#94a3b8' }}>Based on trending items & kid interests</p>
                            </div>

                            <div className="suggestions-grid" style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                                gap: '1rem',
                                marginTop: '1rem'
                            }}>
                                {loadingSuggestions ? (
                                    <p>Analyzing trends...</p>
                                ) : suggestions.length > 0 ? (
                                    suggestions.map((item, idx) => (
                                        <div key={idx} className="suggestion-card" style={{
                                            background: 'rgba(255, 255, 255, 0.05)',
                                            padding: '1rem',
                                            borderRadius: '12px',
                                            border: '1px solid rgba(255, 255, 255, 0.1)',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '0.5rem'
                                        }}>
                                            <div style={{ height: '80px', display: 'flex', justifyContent: 'center' }}>
                                                <img src={item.image_url} alt="" style={{ maxHeight: '100%', borderRadius: '4px' }} />
                                            </div>
                                            <h4 style={{ fontSize: '0.9rem', margin: 0 }}>{item.name}</h4>
                                            <p style={{ fontSize: '0.8rem', color: '#94a3b8', margin: 0 }}>${(item.price_cents / 100).toFixed(2)}</p>
                                            <div style={{ fontSize: '0.7rem', color: '#10b981' }}>Age: {item.min_age}-{item.max_age}</div>
                                            <button
                                                onClick={() => handleAddProduct(item)}
                                                className="btn-tiny"
                                                style={{ marginTop: 'auto' }}
                                            >
                                                + Stock This
                                            </button>
                                        </div>
                                    ))
                                ) : (
                                    <p>Define kid interests to see better suggestions.</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="dashboard-card">
                    <h2>Publish System Broadcast</h2>
                    {message && (
                        <div role="alert" style={{
                            padding: '1rem',
                            borderRadius: '0.5rem',
                            marginBottom: '1rem',
                            background: message.type === 'success' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                            color: message.type === 'success' ? '#10b981' : '#ef4444',
                            border: `1px solid ${message.type === 'success' ? '#10b981' : '#ef4444'}`
                        }}>
                            {message.text}
                        </div>
                    )}
                    <form className="publish-form" onSubmit={handlePublish}>
                        <input
                            type="text"
                            placeholder="Announcement Title"
                            value={publishData.title}
                            onChange={(e) => setPublishData({ ...publishData, title: e.target.value })}
                            required
                            aria-label="Announcement title"
                        />
                        <textarea
                            rows="4"
                            placeholder="Message body..."
                            value={publishData.body}
                            onChange={(e) => setPublishData({ ...publishData, body: e.target.value })}
                            required
                            aria-label="Announcement message body"
                        ></textarea>
                        <select
                            value={publishData.target}
                            onChange={(e) => setPublishData({ ...publishData, target: e.target.value })}
                            aria-label="Broadcast target audience"
                        >
                            <option value="all">Broadcast to Everyone</option>
                            <option value="parents">Parents Only</option>
                            <option value="kids">Kids Only</option>
                        </select>
                        <button type="submit" disabled={publishing}>
                            {publishing ? 'Publishing...' : 'Publish Announcement'}
                        </button>
                    </form>
                </div>

                {/* Feedback Management Section */}
                <div className="dashboard-card" style={{ gridColumn: 'span 2' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                        <h2>💬 User Feedback</h2>
                        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                            {feedbackStats && (
                                <span style={{ fontSize: '0.85rem', color: '#94a3b8' }}>
                                    {feedbackStats.total} total |
                                    {feedbackStats.by_status?.find(s => s.status === 'new')?.count || 0} new
                                </span>
                            )}
                            <select
                                value={feedbackFilter}
                                onChange={(e) => setFeedbackFilter(e.target.value)}
                                aria-label="Filter feedback by status"
                                style={{ padding: '6px 12px', borderRadius: '6px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: 'white' }}
                            >
                                <option value="">All Status</option>
                                <option value="new">New</option>
                                <option value="reviewing">Reviewing</option>
                                <option value="in_progress">In Progress</option>
                                <option value="done">Done</option>
                                <option value="declined">Declined</option>
                            </select>
                        </div>
                    </div>

                    {feedbackLoading ? (
                        <p>Loading feedback...</p>
                    ) : feedbackList.length === 0 ? (
                        <p style={{ color: '#94a3b8' }}>No feedback yet. Users can submit from their dashboard.</p>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxHeight: '400px', overflowY: 'auto' }}>
                            {feedbackList
                                .filter(f => !feedbackFilter || f.status === feedbackFilter)
                                .map((item) => (
                                    <div key={item.id} style={{
                                        background: 'rgba(255, 255, 255, 0.05)',
                                        padding: '1rem',
                                        borderRadius: '12px',
                                        border: '1px solid rgba(255, 255, 255, 0.1)'
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
                                            <div style={{ flex: 1 }}>
                                                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.5rem' }}>
                                                    <span style={{
                                                        background: item.type === 'bug' ? '#ef4444' : '#6366f1',
                                                        color: 'white',
                                                        padding: '2px 8px',
                                                        borderRadius: '4px',
                                                        fontSize: '0.7rem',
                                                        fontWeight: '600'
                                                    }}>
                                                        {item.type === 'bug' ? '🐛 Bug' : '💡 Feature'}
                                                    </span>
                                                    <span style={{
                                                        background: item.status === 'new' ? '#f59e0b' : item.status === 'done' ? '#10b981' : '#64748b',
                                                        color: 'white',
                                                        padding: '2px 8px',
                                                        borderRadius: '4px',
                                                        fontSize: '0.7rem'
                                                    }}>
                                                        {item.status}
                                                    </span>
                                                    <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>by {item.username || 'Unknown'}</span>
                                                </div>
                                                <h4 style={{ margin: '0 0 0.25rem', fontSize: '1rem' }}>{item.title}</h4>
                                                {item.description && (
                                                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#94a3b8' }}>{item.description}</p>
                                                )}
                                                <p style={{ margin: '0.5rem 0 0', fontSize: '0.75rem', color: '#64748b' }}>
                                                    {new Date(item.created_at).toLocaleDateString()}
                                                </p>
                                            </div>
                                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                {item.status === 'new' && (
                                                    <button
                                                        onClick={() => updateFeedbackStatus(item.id, 'reviewing')}
                                                        style={{ background: '#3b82f6', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem' }}
                                                    >
                                                        Review
                                                    </button>
                                                )}
                                                {(item.status === 'reviewing' || item.status === 'in_progress') && (
                                                    <>
                                                        <button
                                                            onClick={() => updateFeedbackStatus(item.id, 'done')}
                                                            style={{ background: '#10b981', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem' }}
                                                        >
                                                            ✓ Done
                                                        </button>
                                                        <button
                                                            onClick={() => updateFeedbackStatus(item.id, 'declined')}
                                                            style={{ background: '#64748b', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem' }}
                                                        >
                                                            Decline
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminDashboard;
